import { HttpClient, HttpParams } from '@angular/common/http';
// Angular Imports
import { Injectable } from '@angular/core';
import { BaseHttpService } from '@core/http/base-http.service';
// App Imports
import {
  AttributeDescription,
  CustomAttributeValue,
} from '@shared/interfaces/attribute-description.interface';
import { CommunityDeltaPayload } from '@shared/interfaces/community.interface';
import { DirectMessage } from '@shared/interfaces/direct-message.interface';
import { ImageInterface } from '@shared/interfaces/file.interface';
import { JuryRating } from '@shared/interfaces/jury-form.interface';
import { ApiGetResponse } from '@shared/interfaces/responses/ApiResponse.interface';
import {
  VentureInterface,
  VenturePhase,
} from '@shared/interfaces/venture.interface';
import { FormUtils } from '@shared/utils/form-utils';
import { StringUtils } from '@shared/utils/string-utils';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class VentureHttpService extends BaseHttpService<VentureInterface> {
  constructor(protected http: HttpClient) {
    super(http);
  }

  get() {
    return super.gets().pipe(
      map((items = []) => {
        return items.map(item => this.tranformData(item));
      })
    );
  }

  paginate(
    params: HttpParams = null,
    innerPath: string | null = null
  ): Observable<ApiGetResponse<VentureInterface>> {
    return super.paginate(params, innerPath).pipe(
      map((res: ApiGetResponse<VentureInterface>) => {
        return this.handlePaginateResponse(res);
      })
    );
  }

  shouldShowContent(
    attributes: AttributeDescription[],
    item: VentureInterface,
    keys: string[]
  ): boolean {
    for (const key of keys) {
      const isVisibleInSystem = FormUtils.getFieldOptions(
        attributes,
        key
      )?.isVisibleInSystem;
      if (
        isVisibleInSystem ||
        item[StringUtils.toLowerCaseFirstLetter(key)] !== null
      ) {
        return true;
      }
    }
  }

  paginateX(
    params: any = null,
    innerPath: string | null = null
  ): Observable<ApiGetResponse<VentureInterface>> {
    return super.paginateX(params, innerPath).pipe(
      map((res: ApiGetResponse<VentureInterface>) => {
        return this.handlePaginateResponse(res);
      })
    );
  }

  paginateAsPost(
    params: any = null,
    innerPath: string | null = null
  ): Observable<ApiGetResponse<VentureInterface>> {
    return super.paginateAsPost(params, innerPath).pipe(
      map((res: ApiGetResponse<VentureInterface>) => {
        return this.handlePaginateResponse(res);
      })
    );
  }

  tranformData(venture: VentureInterface): VentureInterface {
    super.tranformData(venture);
    venture.characteristicsArray = [];
    for (const key of VENTURE_CHARACTERISTICS) {
      const data = venture[
        StringUtils.toLowerCaseFirstLetter(key)
      ] as CustomAttributeValue;
      if (data) {
        venture.characteristicsArray.push(data);
      }
    }
    return venture;
  }

  getApiRootPath(): string {
    return 'ventures';
  }

  savePhases(
    ventureId: number,
    payload: Record<string, VenturePhase[]>
  ): Observable<unknown> {
    const url = `${this.apiUrl}/${ventureId}/phases`;

    return this.http.put(url, payload);
  }

  getDemands(): string[] {
    return [
      'Sponsorship',
      'Funding',
      'Market Access / Channel / Local Representatives / Franchisers / Licensees',
      'Pilot Customer / Customers',
      'Technology',
      'Expertise',
      'Acceleration',
      'Professionalization',
      'Services',
    ];
  }

  sendGetInTouchMessage(
    ventureId: number,
    payload: DirectMessage
  ): Observable<any> {
    const url = `${this.apiUrl}/${ventureId}/messages`;
    return this.http.post(url, payload);
  }

  submitVentureToChallenge(
    ventureId: number,
    challengeId: number
  ): Observable<any> {
    const url = `${this.apiUrl}/${ventureId}/submit`;
    const payload = {
      orgId: challengeId,
    };

    return this.http.post(url, payload);
  }

  getDefaultBanner(submittedOrgId: number[] = []): Observable<ImageInterface> {
    const url = `${this.apiUrl}/default-banner`;

    return this.http.post<ImageInterface>(url, { submittedOrgId });
  }

  updateVentureCommunity(
    ventureId: number,
    payload: CommunityDeltaPayload
  ): Observable<void> {
    const url = `${this.apiUrl}/${ventureId}/update-venture-community`;

    return this.http.post<void>(url, payload);
  }

  removeSubmittedOrg(ventureId: number, orgIds: number[]): Observable<void> {
    const url = `${this.apiUrl}/${ventureId}/remove-submitted-org`;

    return this.http.post<void>(url, {
      orgIds,
    });
  }

  getVentureUiTemplate(
    organizationId: number,
    leadOrganizationId?: number
  ): Observable<string> {
    const url = `${this.apiUrl}/ui-template`;
    const params = new HttpParams();

    params.set('organizationId', organizationId);
    if (leadOrganizationId) {
      params.set('leadOrganizationId', leadOrganizationId);
    }

    return this.http.get<string>(url, {
      params,
    });
  }

  rateVenture(id: number, payload: JuryRating): Observable<void> {
    const url = `${this.apiUrl}/${id}/rating`;

    return this.http.post<void>(url, payload);
  }

  getVentureRating(id: number, challengeId: number): Observable<JuryRating> {
    const url = `${this.apiUrl}/${id}/rating`;

    return this.http.get<JuryRating>(url, {
      params: {
        challengeId,
      },
    });
  }
}

export const VENTURE_CHARACTERISTICS = [
  'BusinessPotential',
  'BusinessReach',
  // 'BusinessScalability',
  'BusinessCustomers',
  'BusinessKind',
  'ExpectedTimeToMarket',
  'MWhSavings',
  'CO2Savings',
];

export const VENTURE_REQUIRED_KEY = ['Title', 'Image', 'Status'];

export const VENTURE_CUSTOM_ATTRIBUTE_ORDER_OF_PROPERTY_NAMES = [
  'ContactOfEconomicPartner',
  'BachelorThesisOfNotAParttimeStudent',
  'StartDate',
  'Deadline',
  'ProjectType',
  'FocusTopics',
  'LookingFor',
  'TermsAndConditionsConfirmation',
];
